import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, BellIcon, XIcon, SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, BriefcaseIcon, ShoppingBagIcon, UserIcon, LifeBuoyIcon, LockIcon, LogOutIcon } from 'lucide-react';
import { Logo } from './Logo';
import { RegionSelector } from './RegionSelector';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: boolean;
};
export function MobileHeader() {
  const {
    hub
  } = useBusiness();
  const {
    isAuthenticated,
    openLogin,
    requireAuth,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Close the drawer whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  const navItems: NavItem[] = [{
    to: '/',
    label: 'Discover',
    icon: SparklesIcon
  }, {
    to: '/requests',
    label: 'Requests',
    icon: FileTextIcon
  }, {
    to: '/saved',
    label: 'Saved',
    icon: BookmarkIcon
  }, {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquareIcon
  }, {
    to: '/business',
    label: 'Business Hub',
    icon: BriefcaseIcon,
    badge: hub?.isNew
  }, ...(hub?.active ? [{
    to: `/shop/${hub.shopSlug || 'shop'}`,
    label: 'My Shop',
    icon: ShoppingBagIcon
  } as NavItem] : []), {
    to: '/profile',
    label: 'Profile',
    icon: UserIcon
  }];
  const handleNav = (to: string) => {
    setOpen(false);
    requireAuth(() => navigate(to));
  };
  return <>
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3" data-id="element-2053">
        <div className="flex items-center justify-between" data-id="element-2054">
          <div className="flex items-center gap-2" data-id="element-2055">
            <button onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} className="p-2 -ml-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2056">
              <MenuIcon className="w-5 h-5 text-slate-600" data-id="element-2057" />
            </button>
            <Logo size="sm" data-id="element-2058" />
          </div>
          <div className="flex items-center gap-2" data-id="element-2059">
            <RegionSelector compact data-id="element-2060" />
            <button aria-label="Notifications" className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2061">
              <BellIcon className="w-5 h-5 text-slate-600" data-id="element-2062" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence data-id="element-2063">
        {open && <div className="lg:hidden fixed inset-0 z-[60]" data-id="element-2064">
            {/* Overlay */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setOpen(false)} className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" data-id="element-2065" />

            {/* Drawer */}
            <motion.aside initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 34
        }} className="absolute top-0 left-0 h-full w-[82%] max-w-sm bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true" data-id="element-2066">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100" data-id="element-2067">
                <Logo size="sm" data-id="element-2068" />
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" data-id="element-2069">
                  <XIcon className="w-5 h-5" data-id="element-2070" />
                </button>
              </div>

              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-id="element-2071">
                {navItems.map(item => {
              const locked = !isAuthenticated && item.to !== '/';
              if (locked) {
                return <button key={item.to} onClick={() => handleNav(item.to)} className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2072">
                        <item.icon className="w-5 h-5" data-id="element-2073" />
                        <span className="flex-1 text-left" data-id="element-2074">{item.label}</span>
                        <LockIcon className="w-4 h-4 text-slate-300" data-id="element-2075" />
                      </button>;
              }
              return <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className={({
                isActive
              }) => `relative flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${item.badge ? 'bg-primary-indigo/5 text-primary-indigo font-medium ring-1 ring-primary-indigo/30' : isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2076">
                      {({
                  isActive
                }) => <>
                          <item.icon className={`w-5 h-5 ${isActive || item.badge ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2077" />
                          <span className="flex-1" data-id="element-2078">{item.label}</span>
                          {item.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-primary-indigo text-white px-2 py-0.5 rounded-full" data-id="element-2079">
                              New
                            </span>}
                        </>}
                    </NavLink>;
            })}

                {/* Help */}
                {isAuthenticated ? <NavLink to="/help" onClick={() => setOpen(false)} className={({
              isActive
            }) => `flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2080">
                    {({
                isActive
              }) => <>
                        <LifeBuoyIcon className={`w-5 h-5 ${isActive ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2081" />
                        <span className="flex-1" data-id="element-2082">Help</span>
                      </>}
                  </NavLink> : <button onClick={() => {
              setOpen(false);
              requireAuth(() => navigate('/help'));
            }} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2083">
                    <LifeBuoyIcon className="w-5 h-5" data-id="element-2084" />
                    <span className="flex-1 text-left" data-id="element-2085">Help</span>
                    <LockIcon className="w-4 h-4 text-slate-300" data-id="element-2086" />
                  </button>}
              </nav>

              {/* Auth action */}
              <div className="px-3 py-4 border-t border-slate-100" data-id="element-2087">
                {isAuthenticated ? <button onClick={() => {
              setOpen(false);
              logout();
              navigate('/');
            }} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-error hover:bg-error/5 transition-colors" data-id="element-2088">
                    <LogOutIcon className="w-5 h-5" data-id="element-2089" />
                    <span className="flex-1 text-left font-medium" data-id="element-2090">
                      Log out
                    </span>
                  </button> : <button onClick={() => {
              setOpen(false);
              openLogin();
            }} className="w-full py-3.5 bg-primary-indigo text-white font-medium rounded-xl hover:bg-primary-indigo/90 transition-colors" data-id="element-2091">
                    Log in / Sign up
                  </button>}
              </div>
            </motion.aside>
          </div>}
      </AnimatePresence>
    </>;
}